// Solution types modal. Rename via inline edit (debounced PATCH on
// blur), add via the bottom row, remove via the × button (soft-
// delete server-side when referenced).
import { useEffect, useState } from "react";
import {
  createSolutionType,
  deleteSolutionType,
  updateSolutionType,
  useSolutionTypes,
} from "../hooks/useProductionApi";

interface Props {
  open: boolean;
  onClose(): void;
}

export function SolutionTypesModal({ open, onClose }: Props) {
  const { data, refetch, error } = useSolutionTypes();
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  if (!open) return null;

  const types = data?.types ?? [];

  const rename = async (id: number, name: string) => {
    setActionError(null);
    setBusyId(id);
    try {
      await updateSolutionType(id, { name });
      await refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const add = async () => {
    const v = newName.trim();
    if (!v) return;
    setActionError(null);
    try {
      await createSolutionType({ name: v, sortOrder: types.length, active: true });
      setNewName("");
      await refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (id: number, name: string) => {
    if (!window.confirm(`Remove "${name}"? Jobs using it keep the value but the type drops from the picker.`)) {
      return;
    }
    setBusyId(id);
    setActionError(null);
    try {
      await deleteSolutionType(id);
      await refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ width: 420 }}>
        <div className="modal-header">
          <h2>Manage Solution Types</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", marginBottom: 14, lineHeight: 1.5 }}>
            Add, rename, or remove the solution types available when creating jobs.
          </p>
          {(error ?? actionError) && (
            <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
              {actionError ?? error}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            {types.map((t) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--bsp-surface-muted)", border: "1px solid var(--bsp-border)", borderRadius: 4, padding: "8px 11px" }}>
                <input
                  type="text"
                  defaultValue={t.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (!v) {
                      // Empty rename — restore the original so the UI
                      // never shows a blank field bound to a live name.
                      e.target.value = t.name;
                      return;
                    }
                    if (v !== t.name) void rename(t.id, v);
                  }}
                  disabled={busyId === t.id}
                  style={{ flex: 1, border: "1px solid var(--bsp-border-strong)", borderRadius: 2, padding: "5px 8px", fontFamily: "var(--bsp-font-sans)", fontSize: 12 }}
                />
                <button type="button" className="btn-sm danger" onClick={() => void remove(t.id, t.name)} disabled={busyId === t.id}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
              placeholder="New type name…"
              style={{ flex: 1, border: "1px solid var(--bsp-border-strong)", borderRadius: 4, padding: "8px 11px", fontFamily: "var(--bsp-font-sans)", fontSize: 13 }}
            />
            <button type="button" className="btn-submit" onClick={() => void add()}>Add</button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
